import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import CheckIcon from "@mui/icons-material/Check";
import { useSnapshot } from "./snapshot.context";
import { SnapshotPipelineStep } from "./SnapshotPipelineStep";
import type { SnapshotPipelineStepTone } from "./SnapshotPipelineStep";

interface SnapshotPipelineProps {
  children?: ReactNode;
}

/**
 * A Snapshot page's pipeline strip above its sections: "Upload" and "Build snapshots" from the
 * sections' summaries, then the page's own steps as children (SnapshotPipelineStep).
 *
 *   <SnapshotPipeline>
 *     <OrderSnapshotStatusImport />
 *   </SnapshotPipeline>
 */
export const SnapshotPipeline = (props: SnapshotPipelineProps) => {
  const { children } = props;
  const snapshot = useSnapshot();
  const dateLabel = snapshot.dateLabel;
  const summaries = Object.values(snapshot.sectionSummaries);

  const fileCount = summaries.reduce((total, summary) => total + summary.fileCount, 0);
  const withFiles = summaries.filter((summary) => summary.fileCount > 0);
  const upToDate = withFiles.filter((summary) => summary.status === "ok").length;
  const allUpToDate = withFiles.length > 0 && upToDate === withFiles.length;

  let uploadMarker: ReactNode = "1";
  let uploadTone: SnapshotPipelineStepTone = "neutral";
  if (fileCount > 0) {
    uploadMarker = <CheckIcon sx={{ fontSize: 16 }} />;
    uploadTone = "done";
  }

  let buildMarker: ReactNode = "2";
  let buildTone: SnapshotPipelineStepTone = "neutral";
  let buildValue = "Nothing to build";
  let buildSub = `No files for ${dateLabel}`;
  if (allUpToDate) {
    buildMarker = <CheckIcon sx={{ fontSize: 16 }} />;
    buildTone = "done";
    buildValue = `${upToDate} of ${withFiles.length} up to date`;
    buildSub = `Ready for ${dateLabel}`;
  }
  else if (withFiles.length > 0) {
    buildTone = "attention";
    buildValue = `${upToDate} of ${withFiles.length} up to date`;
    buildSub = `${withFiles.length - upToDate} need building`;
  }

  let columns = "1fr 1fr";
  if (children) columns = "1fr 1fr 1.6fr";

  return (
    <Box
      sx={{
        display: "grid",
        flexShrink: 0,
        gap: 1,
        gridTemplateColumns: { xs: "1fr", md: columns },
        "& > :first-of-type": { pl: 0 },
      }}
    >
      <SnapshotPipelineStep
        label="1 · Upload"
        marker={uploadMarker}
        sub={`${withFiles.length} of ${summaries.length} sources have files`}
        tone={uploadTone}
        value={`${fileCount} ${fileCount === 1 ? "file" : "files"}`}
      />
      <SnapshotPipelineStep
        label="2 · Build snapshots"
        marker={buildMarker}
        sub={buildSub}
        tone={buildTone}
        value={buildValue}
      />
      {children}
    </Box>
  );
};
